import React, { useState, useEffect } from "react";
import { useApi } from "../../hooks/useApi";
import { useTheme } from "../../contexts/ThemeContext";
import { AdminDesignSystem, ADMIN_ROOT } from "./adminDesignSystem";
import AdminSidebar from "./AdminSidebar";
import toast from "react-hot-toast";
import {
  InputNumber,
  Select,
  DatePicker,
  ConfigProvider,
  theme as antdTheme,
  Switch,
} from "antd";
import dayjs from "dayjs";
import {
  Tag,
  Plus,
  CheckCircle,
  XCircle,
  Calendar,
  Percent,
  Copy,
  Trash2,
  Users,
  Sparkles,
  X,
  TrendingUp,
  Clock,
  AlertCircle,
  Edit3,
  IndianRupee,
  Power,
} from "lucide-react";

const RazorpayCouponManagement = () => {
  const api = useApi();
  const { mode } = useTheme();
  const isDark = mode === "dark";

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [filterLinked, setFilterLinked] = useState(true);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    discountType: "percent",
    discountValue: "",
    currency: "INR",
    duration: "once",
    durationInMonths: "",
    maxRedemptions: "",
    validTill: "",
    minMemberCount: "",
    maxMemberCount: "",
    applicablePlans: [],
    razorpayOfferId: "",
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/v1/razorpay-billing/coupons");
      setCoupons(response.data.data.coupons || []);
    } catch (error) {
      toast.error("Failed to fetch Razorpay coupons");
      console.error("Error fetching Razorpay coupons:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    try {
      if (!formData.code.trim()) {
        toast.error("Coupon code is required");
        return;
      }
      if (!formData.name.trim()) {
        toast.error("Coupon name is required");
        return;
      }

      const dv = parseFloat(formData.discountValue);
      if (isNaN(dv) || dv <= 0) {
        toast.error("Discount value must be greater than 0");
        return;
      }
      if (formData.discountType === "percent" && dv > 100) {
        toast.error("Percentage must be between 0.01 and 100");
        return;
      }

      if (!formData.razorpayOfferId || !formData.razorpayOfferId.trim()) {
        toast.error("Razorpay Offer ID is required. Create an offer in Razorpay Dashboard first.");
        return;
      }

      // Duration validation commented out - managed via Razorpay Dashboard
      // if (formData.duration === "repeating" && !formData.durationInMonths) {
      //   toast.error("Duration in months is required for repeating coupons");
      //   return;
      // }

      const payload = {
        code: formData.code.trim().toUpperCase(),
        name: formData.name.trim(),
        discountType: formData.discountType,
        discountValue: dv,
        currency: formData.currency || "INR",
        duration: formData.duration,
      };

      if (formData.duration === "repeating") {
        payload.durationInMonths = parseInt(formData.durationInMonths);
      }
      if (formData.maxRedemptions) {
        payload.maxRedemptions = parseInt(formData.maxRedemptions);
      }
      if (formData.validTill) {
        payload.validTill = formData.validTill;
      }
      if (formData.minMemberCount) {
        payload.minMemberCount = parseInt(formData.minMemberCount);
      }
      if (formData.maxMemberCount) {
        payload.maxMemberCount = parseInt(formData.maxMemberCount);
      }
      if (formData.applicablePlans && formData.applicablePlans.length > 0) {
        payload.applicablePlans = formData.applicablePlans;
      }
      if (formData.razorpayOfferId && formData.razorpayOfferId.trim()) {
        payload.razorpayOfferId = formData.razorpayOfferId.trim();
      }

      if (editingCoupon) {
        await api.put(
          `/api/v1/razorpay-billing/coupons/${editingCoupon.id}`,
          payload,
        );
        toast.success("Coupon updated successfully!");
      } else {
        await api.post("/api/v1/razorpay-billing/create-coupon", payload);
        toast.success("Coupon created successfully!");
      }

      setShowCreateModal(false);
      setEditingCoupon(null);
      resetForm();
      fetchCoupons();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          `Failed to ${editingCoupon ? "update" : "create"} coupon`,
      );
      console.error("Error saving Razorpay coupon:", error);
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    try {
      setDeletingId(couponId);
      await api.delete(`/api/v1/razorpay-billing/coupons/${couponId}`);
      toast.success("Coupon deleted successfully!");
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete coupon");
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      await api.put(`/api/v1/razorpay-billing/coupons/${coupon.id}`, {
        isActive: !coupon.valid,
      });
      toast.success(
        `Coupon ${coupon.valid ? "deactivated" : "activated"} successfully!`,
      );
      fetchCoupons();
    } catch (error) {
      toast.error("Failed to update coupon status");
    }
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code || "",
      name: coupon.name || "",
      discountType: coupon.discountType || "percent",
      discountValue: coupon.discountValue ? coupon.discountValue.toString() : "",
      currency: coupon.currency || "INR",
      duration: coupon.duration || "once",
      durationInMonths: coupon.duration_in_months
        ? coupon.duration_in_months.toString()
        : "",
      maxRedemptions: coupon.max_redemptions
        ? coupon.max_redemptions.toString()
        : "",
      validTill: coupon.redeem_by
        ? dayjs(coupon.redeem_by * 1000).format("YYYY-MM-DDTHH:mm")
        : "",
      minMemberCount: coupon.min_member_count
        ? coupon.min_member_count.toString()
        : "",
      maxMemberCount: coupon.max_member_count
        ? coupon.max_member_count.toString()
        : "",
      applicablePlans: coupon.applicablePlans || [],
      razorpayOfferId: coupon.razorpayOfferId || "",
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      code: "",
      name: "",
      discountType: "percent",
      discountValue: "",
      currency: "INR",
      duration: "once",
      durationInMonths: "",
      maxRedemptions: "",
      validTill: "",
      minMemberCount: "",
      maxMemberCount: "",
      applicablePlans: [],
      razorpayOfferId: "",
    });
    setEditingCoupon(null);
  };

  const copyCouponCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Coupon code copied to clipboard!");
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Never";
    return new Date(timestamp * 1000).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getDiscountText = (coupon) => {
    if (coupon.discountType === "percent" && coupon.discountValue) {
      const pct =
        coupon.discountValue % 1 === 0
          ? coupon.discountValue.toString()
          : coupon.discountValue.toFixed(2);
      return `${pct}% OFF`;
    } else if (coupon.discountType === "flat" && coupon.discountValue) {
      return `₹${coupon.discountValue} OFF`;
    }
    return "Discount";
  };

  const getDurationBadge = (duration) => {
    const badges = {
      once: {
        text: "One-time",
        color: isDark
          ? "bg-blue-500/20 text-blue-400"
          : "bg-blue-100 text-blue-700",
      },
      repeating: {
        text: "Repeating",
        color: isDark
          ? "bg-purple-500/20 text-purple-400"
          : "bg-purple-100 text-purple-700",
      },
      forever: {
        text: "Forever",
        color: isDark
          ? "bg-green-500/20 text-green-400"
          : "bg-green-100 text-green-700",
      },
    };
    return badges[duration] || badges.once;
  };

  return (
    <div className={`${ADMIN_ROOT} h-screen flex overflow-hidden bg-black`}>
      <AdminDesignSystem />
      <AdminSidebar activeKey="razorpay-coupons" />

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="w-full px-5 sm:px-6">
              {/* Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className={`p-3 rounded-xl ${isDark ? "bg-orange-500/20" : "bg-orange-100"}`}
                    >
                      <IndianRupee
                        className={`h-6 w-6 ${isDark ? "text-orange-400" : "text-orange-600"}`}
                      />
                    </div>
                    <div>
                      <h1
                        className={`text-3xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        Razorpay Coupons
                      </h1>
                      <p
                        className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                      >
                        {coupons.length}{" "}
                        {coupons.length === 1 ? "coupon" : "coupons"} available
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setFilterLinked(!filterLinked)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all duration-200 border ${
                      filterLinked
                        ? isDark
                          ? "bg-orange-500/20 border-orange-500/50 text-orange-400"
                          : "bg-orange-50 border-orange-300 text-orange-700"
                        : isDark
                          ? "bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600"
                          : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    {filterLinked ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <AlertCircle className="h-4 w-4" />
                    )}
                    {filterLinked ? "Linked Only" : "Show All"}
                  </button>
                  <button
                    onClick={() => {
                      resetForm();
                      setShowCreateModal(true);
                    }}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 !text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                  >
                    <Plus className="h-5 w-5" />
                    Create Coupon
                  </button>
                </div>
              </div>

              {/* Coupons List */}
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div
                    className={`animate-spin rounded-full h-12 w-12 border-b-2 ${isDark ? "border-orange-400" : "border-orange-600"}`}
                  ></div>
                  <p
                    className={`mt-4 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                  >
                    Loading Razorpay coupons...
                  </p>
                </div>
              ) : coupons.length === 0 ? (
                <div
                  className={`text-center py-20 rounded-2xl border-2 border-dashed ${isDark ? "border-gray-700 bg-gray-800/50" : "border-gray-300 bg-gray-50"}`}
                >
                  <div
                    className={`inline-flex p-4 rounded-full mb-4 ${isDark ? "bg-gray-800" : "bg-gray-100"}`}
                  >
                    <IndianRupee
                      className={`h-8 w-8 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                    />
                  </div>
                  <h3
                    className={`text-xl font-semibold mb-2 ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    No Razorpay coupons found
                  </h3>
                  <p
                    className={`mb-6 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                  >
                    Create your first Razorpay coupon for Indian customers
                  </p>
                  <button
                    onClick={() => {
                      resetForm();
                      setShowCreateModal(true);
                    }}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors"
                  >
                    <Plus className="h-5 w-5" />
                    Create Your First Coupon
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {coupons.filter((c) => !filterLinked || c.razorpayOfferId).map((coupon) => {
                    const discountText = getDiscountText(coupon);
                    const durationBadge = getDurationBadge(coupon.duration);
                    const isExpired =
                      coupon.redeem_by &&
                      new Date(coupon.redeem_by * 1000) < new Date();

                    return (
                      <div
                        key={coupon.id}
                        className={`group relative overflow-hidden rounded-2xl border transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] ${
                          isDark
                            ? "bg-gradient-to-br from-gray-800 to-gray-800/80 border-gray-700 hover:border-orange-500/50"
                            : "bg-white border-gray-200 hover:border-orange-300 shadow-lg hover:shadow-xl"
                        }`}
                      >
                        {/* Top gradient bar */}
                        <div
                          className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${
                            coupon.discountType === "percent"
                              ? "from-orange-500 to-amber-500"
                              : "from-emerald-500 to-teal-500"
                          }`}
                        ></div>

                        {/* Status + Actions */}
                        <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
                          <button
                            onClick={() => handleEditCoupon(coupon)}
                            className={`p-1.5 rounded-lg transition-colors ${
                              isDark
                                ? "hover:bg-gray-700 text-gray-400 hover:text-orange-400"
                                : "hover:bg-gray-100 text-gray-500 hover:text-orange-600"
                            }`}
                            title="Edit Coupon"
                          >
                            <Edit3 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleToggleActive(coupon)}
                            className={`p-1.5 rounded-lg transition-colors ${
                              isDark
                                ? "hover:bg-gray-700 text-gray-400 hover:text-yellow-400"
                                : "hover:bg-gray-100 text-gray-500 hover:text-yellow-600"
                            }`}
                            title={coupon.valid ? "Deactivate" : "Activate"}
                          >
                            <Power className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Delete coupon "${coupon.code}"?`,
                                )
                              ) {
                                handleDeleteCoupon(coupon.id);
                              }
                            }}
                            disabled={deletingId === coupon.id}
                            className={`p-1.5 rounded-lg transition-colors ${
                              isDark
                                ? "hover:bg-red-500/20 text-gray-400 hover:text-red-400"
                                : "hover:bg-red-50 text-gray-500 hover:text-red-600"
                            }`}
                            title="Delete Coupon"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                          {coupon.valid && !isExpired ? (
                            <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-500/20 border border-green-500/30">
                              <CheckCircle className="h-3.5 w-3.5 text-green-400" />
                              <span className="text-xs font-medium text-green-400">
                                Active
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500/20 border border-red-500/30">
                              <XCircle className="h-3.5 w-3.5 text-red-400" />
                              <span className="text-xs font-medium text-red-400">
                                {isExpired ? "Expired" : "Inactive"}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="p-6 pt-8">
                          {/* Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <div
                                  className={`p-2 rounded-lg ${isDark ? "bg-orange-500/20" : "bg-orange-100"}`}
                                >
                                  <IndianRupee
                                    className={`h-4 w-4 ${isDark ? "text-orange-400" : "text-orange-600"}`}
                                  />
                                </div>
                                <h3
                                  className={`font-bold text-lg truncate ${isDark ? "text-white" : "text-gray-900"}`}
                                >
                                  {coupon.name || "Unnamed Coupon"}
                                </h3>
                              </div>
                              <div
                                className={`text-2xl font-bold mb-3 ${
                                  coupon.discountType === "percent"
                                    ? isDark
                                      ? "text-orange-400"
                                      : "text-orange-600"
                                    : isDark
                                      ? "text-emerald-400"
                                      : "text-emerald-600"
                                }`}
                              >
                                {discountText}
                              </div>
                            </div>
                          </div>

                          {/* Coupon Code */}
                          <div
                            className={`mb-4 p-3 rounded-xl ${isDark ? "bg-gray-900/50 border border-gray-700" : "bg-gray-50 border border-gray-200"}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p
                                  className={`text-xs font-medium mb-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                                >
                                  Coupon Code
                                </p>
                                <code
                                  className={`text-sm font-mono font-bold block truncate ${isDark ? "text-orange-400" : "text-orange-600"}`}
                                >
                                  {coupon.code}
                                </code>
                              </div>
                              <button
                                onClick={() => copyCouponCode(coupon.code)}
                                className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
                                  isDark
                                    ? "hover:bg-gray-700 text-gray-400 hover:text-orange-400"
                                    : "hover:bg-gray-100 text-gray-500 hover:text-orange-600"
                                }`}
                                title="Copy Code"
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                            </div>
                          </div>

                          {/* Razorpay Offer Link Status */}
                          <div
                            className={`mb-4 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${
                              coupon.razorpayOfferId
                                ? isDark
                                  ? "bg-green-500/10 border border-green-500/30 text-green-400"
                                  : "bg-green-50 border border-green-200 text-green-700"
                                : isDark
                                  ? "bg-yellow-500/10 border border-yellow-500/30 text-yellow-400"
                                  : "bg-yellow-50 border border-yellow-200 text-yellow-700"
                            }`}
                          >
                            {coupon.razorpayOfferId ? (
                              <>
                                <CheckCircle className="h-3.5 w-3.5" />
                                <span>Razorpay Offer Linked</span>
                              </>
                            ) : (
                              <>
                                <AlertCircle className="h-3.5 w-3.5" />
                                <span>No Razorpay Offer (Local Only)</span>
                              </>
                            )}
                          </div>

                          {/* Details */}
                          <div
                            className={`space-y-3 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            {/* Duration */}
                            <div className="flex items-center gap-2">
                              <Clock
                                className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                              />
                              <span className="font-medium">Duration:</span>
                              <span
                                className={`px-2 py-0.5 rounded-md text-xs font-medium ${durationBadge.color}`}
                              >
                                {durationBadge.text}
                                {coupon.duration === "repeating" &&
                                  coupon.duration_in_months &&
                                  ` (${coupon.duration_in_months}mo)`}
                              </span>
                            </div>

                            {/* Redemptions */}
                            {coupon.max_redemptions && (
                              <div className="flex items-center gap-2">
                                <TrendingUp
                                  className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                                />
                                <span className="font-medium">Uses:</span>
                                <span
                                  className={
                                    isDark ? "text-gray-400" : "text-gray-600"
                                  }
                                >
                                  {coupon.times_redeemed || 0} /{" "}
                                  {coupon.max_redemptions}
                                </span>
                                <div className="flex-1 h-2 rounded-full overflow-hidden bg-gray-700">
                                  <div
                                    className="h-full bg-orange-500 transition-all"
                                    style={{
                                      width: `${Math.min(((coupon.times_redeemed || 0) / coupon.max_redemptions) * 100, 100)}%`,
                                    }}
                                  ></div>
                                </div>
                              </div>
                            )}

                            {/* Expiry */}
                            {coupon.redeem_by && (
                              <div
                                className={`flex items-center gap-2 p-2 rounded-lg ${
                                  isExpired
                                    ? isDark
                                      ? "bg-red-500/10 border border-red-500/20"
                                      : "bg-red-50 border border-red-200"
                                    : isDark
                                      ? "bg-gray-900/50"
                                      : "bg-gray-50"
                                }`}
                              >
                                <Calendar
                                  className={`h-4 w-4 ${isExpired ? "text-red-400" : isDark ? "text-gray-500" : "text-gray-400"}`}
                                />
                                <span className="font-medium">Expires:</span>
                                <span
                                  className={
                                    isExpired
                                      ? "text-red-400 font-medium"
                                      : isDark
                                        ? "text-gray-400"
                                        : "text-gray-600"
                                  }
                                >
                                  {formatDate(coupon.redeem_by)}
                                </span>
                              </div>
                            )}

                            {/* Applicable Plans */}
                            {coupon.applicablePlans &&
                              coupon.applicablePlans.length > 0 && (
                                <div className="flex items-center gap-2">
                                  <Tag
                                    className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                                  />
                                  <span className="font-medium">Plans:</span>
                                  <div className="flex gap-1 flex-wrap">
                                    {coupon.applicablePlans.map((plan) => (
                                      <span
                                        key={plan}
                                        className={`px-2 py-0.5 rounded-md text-xs font-medium capitalize ${isDark ? "bg-orange-500/20 text-orange-400" : "bg-orange-100 text-orange-700"}`}
                                      >
                                        {plan}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                            {/* Member Count */}
                            {(coupon.min_member_count ||
                              coupon.max_member_count) && (
                              <div
                                className={`flex items-center gap-2 p-2 rounded-lg ${isDark ? "bg-gray-900/50" : "bg-gray-50"}`}
                              >
                                <Users
                                  className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                                />
                                <span className="font-medium">Members:</span>
                                <span
                                  className={
                                    isDark ? "text-gray-400" : "text-gray-600"
                                  }
                                >
                                  {coupon.min_member_count &&
                                  coupon.max_member_count
                                    ? `${coupon.min_member_count} - ${coupon.max_member_count}`
                                    : coupon.min_member_count
                                      ? `Min: ${coupon.min_member_count}+`
                                      : `Max: ${coupon.max_member_count}`}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Create / Edit Coupon Modal */}
              {showCreateModal && (
                <div
                  className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                  onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      setShowCreateModal(false);
                      resetForm();
                    }
                  }}
                >
                  <div
                    className={`w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
                      isDark
                        ? "bg-gray-800 border border-gray-700"
                        : "bg-white border border-gray-200"
                    }`}
                  >
                    {/* Modal Header */}
                    <div
                      className={`sticky top-0 z-10 flex items-center justify-between p-6 border-b ${
                        isDark
                          ? "border-gray-700 bg-gray-800"
                          : "border-gray-200 bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`p-2 rounded-lg ${isDark ? "bg-orange-500/20" : "bg-orange-100"}`}
                        >
                          <Sparkles
                            className={`h-5 w-5 ${isDark ? "text-orange-400" : "text-orange-600"}`}
                          />
                        </div>
                        <div>
                          <h2
                            className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}
                          >
                            {editingCoupon
                              ? "Edit Razorpay Coupon"
                              : "Create Razorpay Coupon"}
                          </h2>
                          <p
                            className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                          >
                            {editingCoupon
                              ? "Update this coupon for Indian customers"
                              : "Create a discount coupon for Indian customers"}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setShowCreateModal(false);
                          resetForm();
                        }}
                        className={`p-2 rounded-lg transition-colors ${
                          isDark
                            ? "hover:bg-gray-700 text-gray-400 hover:text-white"
                            : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Modal Body */}
                    <ConfigProvider
                      theme={{
                        algorithm: isDark
                          ? antdTheme.darkAlgorithm
                          : antdTheme.defaultAlgorithm,
                        token: {
                          colorPrimary: "#f97316",
                          borderRadius: 12,
                        },
                      }}
                    >
                      <form
                        onSubmit={handleCreateCoupon}
                        className="p-6 space-y-6"
                      >
                        {/* Coupon Code */}
                        <div>
                          <label
                            className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            Coupon Code{" "}
                            <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            required
                            value={formData.code}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                code: e.target.value.toUpperCase(),
                              })
                            }
                            disabled={!!editingCoupon}
                            className={`w-full px-4 py-3 rounded-xl border transition-colors ${
                              editingCoupon
                                ? isDark
                                  ? "bg-gray-900 border-gray-700 text-gray-500 cursor-not-allowed"
                                  : "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
                                : isDark
                                  ? "bg-gray-700/50 border-gray-600 text-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                                  : "bg-gray-50 border-gray-300 text-gray-900 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                            }`}
                            placeholder="INDIA35"
                          />
                          {editingCoupon && (
                            <p
                              className={`text-xs mt-2 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                            >
                              Coupon code cannot be changed after creation
                            </p>
                          )}
                        </div>

                        {/* Coupon Name */}
                        <div>
                          <label
                            className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            Coupon Name{" "}
                            <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) =>
                              setFormData({ ...formData, name: e.target.value })
                            }
                            className={`w-full px-4 py-3 rounded-xl border transition-colors ${
                              isDark
                                ? "bg-gray-700/50 border-gray-600 text-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                                : "bg-gray-50 border-gray-300 text-gray-900 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                            }`}
                            placeholder="Diwali Sale 2026"
                          />
                        </div>

                        {/* Discount Type */}
                        <div>
                          <label
                            className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            Discount Type{" "}
                            <span className="text-red-400">*</span>
                          </label>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  discountType: "percent",
                                })
                              }
                              className={`p-4 rounded-xl border-2 transition-all ${
                                formData.discountType === "percent"
                                  ? isDark
                                    ? "border-orange-500 bg-orange-500/20"
                                    : "border-orange-500 bg-orange-50"
                                  : isDark
                                    ? "border-gray-600 bg-gray-700/50 hover:border-gray-500"
                                    : "border-gray-300 bg-white hover:border-gray-400"
                              }`}
                            >
                              <Percent
                                className={`h-5 w-5 mb-2 mx-auto ${formData.discountType === "percent" ? "text-orange-500" : isDark ? "text-gray-400" : "text-gray-500"}`}
                              />
                              <div
                                className={`text-sm font-medium ${formData.discountType === "percent" ? "text-orange-500" : isDark ? "text-gray-400" : "text-gray-600"}`}
                              >
                                Percentage
                              </div>
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  discountType: "flat",
                                })
                              }
                              className={`p-4 rounded-xl border-2 transition-all ${
                                formData.discountType === "flat"
                                  ? isDark
                                    ? "border-orange-500 bg-orange-500/20"
                                    : "border-orange-500 bg-orange-50"
                                  : isDark
                                    ? "border-gray-600 bg-gray-700/50 hover:border-gray-500"
                                    : "border-gray-300 bg-white hover:border-gray-400"
                              }`}
                            >
                              <IndianRupee
                                className={`h-5 w-5 mb-2 mx-auto ${formData.discountType === "flat" ? "text-orange-500" : isDark ? "text-gray-400" : "text-gray-500"}`}
                              />
                              <div
                                className={`text-sm font-medium ${formData.discountType === "flat" ? "text-orange-500" : isDark ? "text-gray-400" : "text-gray-600"}`}
                              >
                                Fixed Amount (₹)
                              </div>
                            </button>
                          </div>
                        </div>

                        {/* Discount Value */}
                        <div>
                          <label
                            className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            {formData.discountType === "percent"
                              ? "Percentage Off"
                              : "Amount Off (₹)"}{" "}
                            <span className="text-red-400">*</span>
                          </label>
                          <InputNumber
                            required
                            min={0.01}
                            max={
                              formData.discountType === "percent"
                                ? 100
                                : undefined
                            }
                            step={
                              formData.discountType === "percent" ? 0.01 : 1
                            }
                            precision={
                              formData.discountType === "percent" ? 2 : 0
                            }
                            value={
                              formData.discountValue
                                ? parseFloat(formData.discountValue)
                                : null
                            }
                            onChange={(value) =>
                              setFormData({
                                ...formData,
                                discountValue:
                                  value !== null ? value.toString() : "",
                              })
                            }
                            placeholder={
                              formData.discountType === "percent"
                                ? "34.56"
                                : "500"
                            }
                            size="large"
                            className="w-full"
                            style={{ width: "100%" }}
                            addonAfter={
                              formData.discountType === "percent" ? "%" : "₹"
                            }
                          />
                          <p
                            className={`text-xs mt-2 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                          >
                            {formData.discountType === "percent"
                              ? "Enter percentage (e.g., 34.56 for 34.56% off)"
                              : "Enter flat discount amount in INR"}
                          </p>
                        </div>

                        {/* Razorpay Offer ID */}
                        <div>
                          <label
                            className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            Razorpay Offer ID{" "}
                            <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.razorpayOfferId}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                razorpayOfferId: e.target.value,
                              })
                            }
                            placeholder="e.g. offer_JHD834hjbxzhd38d"
                            className={`w-full px-4 py-2.5 rounded-xl border text-sm transition-colors ${
                              isDark
                                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500 focus:border-orange-500"
                                : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-orange-500"
                            } focus:outline-none focus:ring-2 focus:ring-orange-500/20`}
                          />
                          <p
                            className={`mt-1 text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}
                          >
                            Paste the Offer ID from Razorpay Dashboard (duration, plan, redemptions are managed there)
                          </p>
                        </div>

                        {/* NOTE: Duration, Applicable Plans, Max Redemptions, Expiry Date fields are commented out.
                            These are now managed via Razorpay Dashboard when creating the offer.
                            Uncomment these if local-only coupon support is needed in the future. */}

                        {/* Duration */}
                        {/* <div>
                          <label className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            Duration <span className="text-red-400">*</span>
                          </label>
                          <Select
                            value={formData.duration}
                            onChange={(value) => setFormData({ ...formData, duration: value })}
                            size="large"
                            className="w-full"
                            options={[
                              { label: "Once", value: "once" },
                              { label: "Repeating", value: "repeating" },
                              { label: "Forever", value: "forever" },
                            ]}
                          />
                        </div> */}

                        {/* Duration in Months */}
                        {/* {formData.duration === "repeating" && (
                          <div>
                            <label className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                              Duration in Months <span className="text-red-400">*</span>
                            </label>
                            <InputNumber
                              required
                              min={1}
                              value={formData.durationInMonths ? parseInt(formData.durationInMonths) : null}
                              onChange={(value) => setFormData({ ...formData, durationInMonths: value ? value.toString() : "" })}
                              placeholder="3"
                              size="large"
                              className="w-full"
                              style={{ width: "100%" }}
                            />
                          </div>
                        )} */}

                        {/* Applicable Plans */}
                        {/* <div>
                          <label className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            Applicable Plans <span className="text-gray-500 font-normal">(optional, leave empty for all)</span>
                          </label>
                          <Select
                            mode="multiple"
                            value={formData.applicablePlans}
                            onChange={(value) => setFormData({ ...formData, applicablePlans: value })}
                            size="large"
                            className="w-full"
                            placeholder="All plans"
                            options={[
                              { label: "Standard", value: "standard" },
                              { label: "Premium", value: "premium" },
                            ]}
                          />
                        </div> */}

                        {/* Max Redemptions */}
                        {/* <div>
                          <label className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            Max Redemptions <span className="text-gray-500 font-normal">(optional)</span>
                          </label>
                          <InputNumber
                            min={1}
                            value={formData.maxRedemptions ? parseInt(formData.maxRedemptions) : null}
                            onChange={(value) => setFormData({ ...formData, maxRedemptions: value ? value.toString() : "" })}
                            placeholder="100"
                            size="large"
                            className="w-full"
                            style={{ width: "100%" }}
                          />
                        </div> */}

                        {/* Expiry Date */}
                        {/* <div>
                          <label className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            Expiry Date <span className="text-gray-500 font-normal">(optional)</span>
                          </label>
                          <DatePicker
                            showTime
                            value={formData.validTill ? dayjs(formData.validTill) : null}
                            onChange={(date) => setFormData({ ...formData, validTill: date ? date.format("YYYY-MM-DDTHH:mm") : "" })}
                            format="YYYY-MM-DD HH:mm"
                            placeholder="Select date and time"
                            size="large"
                            className="w-full"
                            style={{ width: "100%" }}
                          />
                        </div> */}

                        {/* Member Count Restrictions - commented out, managed via Razorpay Dashboard */}
                        {/* <div
                          className={`border-t pt-6 ${isDark ? "border-gray-700" : "border-gray-200"}`}
                        >
                          <div className="flex items-center gap-2 mb-4">
                            <Users className={`h-5 w-5 ${isDark ? "text-orange-400" : "text-orange-600"}`} />
                            <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                              Member Count Restrictions
                            </h3>
                            <span className={`text-xs px-2 py-0.5 rounded ${isDark ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-600"}`}>
                              Optional
                            </span>
                          </div>
                          <p className={`text-sm mb-4 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                            Set minimum and/or maximum member count required to use this coupon
                          </p>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                                Minimum Members
                              </label>
                              <InputNumber
                                min={1}
                                value={formData.minMemberCount ? parseInt(formData.minMemberCount) : null}
                                onChange={(value) => setFormData({ ...formData, minMemberCount: value ? value.toString() : "" })}
                                placeholder="5"
                                size="large"
                                className="w-full"
                                style={{ width: "100%" }}
                              />
                            </div>
                            <div>
                              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                                Maximum Members
                              </label>
                              <InputNumber
                                min={1}
                                value={formData.maxMemberCount ? parseInt(formData.maxMemberCount) : null}
                                onChange={(value) => setFormData({ ...formData, maxMemberCount: value ? value.toString() : "" })}
                                placeholder="50"
                                size="large"
                                className="w-full"
                                style={{ width: "100%" }}
                              />
                            </div>
                          </div>
                        </div> */}

                        {/* Modal Footer */}
                        <div
                          className={`flex justify-end gap-3 pt-6 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setShowCreateModal(false);
                              resetForm();
                            }}
                            className={`px-6 py-3 rounded-xl font-medium transition-colors ${
                              isDark
                                ? "border border-gray-600 text-gray-300 hover:bg-gray-700"
                                : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 !text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-200"
                          >
                            {editingCoupon ? "Update Coupon" : "Create Coupon"}
                          </button>
                        </div>
                      </form>
                    </ConfigProvider>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default RazorpayCouponManagement;
